import { useEffect, useRef, useState, useSyncExternalStore } from "react";

// Dates and times written the way the prototype does: "Thu 9 Oct", "7:00 PM WAT", "7:00 – 9:00 PM WAT".
// Classes are planned in WAT, which is the default. A student can pick another zone in Settings; it is
// saved on their account (users.time_zone) and every page then shows times in that zone.
export const WAT = "Africa/Lagos";
const CACHE_KEY = "e1x-time-zone";
export const localZone = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || WAT;
  } catch {
    return WAT;
  }
})();

/** Zones offered in Settings (the prototype's list, plus the UK and the student's own device zone). */
export const ZONES = (() => {
  const list = [
    [WAT, "West Africa Time (Lagos)"],
    ["Africa/Harare", "Central Africa Time (Harare)"],
    ["Africa/Nairobi", "East Africa Time (Nairobi)"],
    ["Europe/London", "UK time (London)"],
    ["America/New_York", "Eastern Time (New York)"],
  ];
  if (!list.some(([tz]) => tz === localZone)) list.push([localZone, `${localZone.split("/").pop().replace(/_/g, " ")} (this device)`]);
  return list;
})();

const valid = (tz) => {
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

// Cached in the browser so the first paint uses the right zone before the profile has loaded.
let current = (() => {
  try {
    const saved = localStorage.getItem(CACHE_KEY);
    return saved && valid(saved) ? saved : WAT;
  } catch {
    return WAT;
  }
})();
const listeners = new Set();

/** The zone in use, e.g. "Africa/Lagos". */
export const zone = () => current;
/** Use a zone everywhere (null or an unknown name means WAT). */
export const setZone = (tz) => {
  const next = tz && valid(tz) ? tz : WAT;
  if (next === current) return;
  current = next;
  try {
    localStorage.setItem(CACHE_KEY, next);
  } catch {
    // Private mode: the profile still has it.
  }
  listeners.forEach((fn) => fn());
};
const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
/** Re-renders the caller when the zone changes. */
export const useZone = () => useSyncExternalStore(subscribe, zone);

/** Short name of a zone at a moment: "WAT", "BST", "EDT", "GMT+2". */
export const zoneLabel = (tz = zone(), at = new Date()) => {
  const african = { [WAT]: "WAT", "Africa/Harare": "CAT", "Africa/Nairobi": "EAT" };
  if (african[tz]) return african[tz];
  // en-US knows American names (EDT), en-GB knows British ones (BST); fall back to "GMT+2" style.
  const name = (locale) => new Intl.DateTimeFormat(locale, { timeZone: tz, timeZoneName: "short" }).formatToParts(at).find((p) => p.type === "timeZoneName")?.value || "";
  try {
    return [name("en-US"), name("en-GB")].find((n) => n && !n.startsWith("GMT") && !n.startsWith("UTC")) || name("en-GB");
  } catch {
    return "";
  }
};

const clock = (d) => d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: zone() });

export const fmtTime = (iso) => (iso ? `${clock(new Date(iso))} ${zoneLabel(zone(), new Date(iso))}`.trim() : "");

/** "7:00 – 9:00 PM WAT" (same half of the day) or "11:00 AM – 1:00 PM WAT". */
export const fmtRange = (startIso, endIso) => {
  if (!startIso) return "";
  if (!endIso) return fmtTime(startIso);
  const a = clock(new Date(startIso));
  const b = clock(new Date(endIso));
  const [aTime, aHalf] = a.split(" ");
  const [, bHalf] = b.split(" ");
  return `${aHalf === bHalf ? aTime : a} – ${b} ${zoneLabel(zone(), new Date(startIso))}`.trim();
};

export const fmtDay = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: zone() }).replace(",", "") : "";

export const fmtDayTime = (iso) => (iso ? `${fmtDay(iso)}, ${fmtTime(iso)}` : "");

/** Calendar date ("2026-10-13") of a moment in the chosen zone. */
export const ymd = (d = new Date()) => new Date(d).toLocaleDateString("en-CA", { timeZone: zone() });
export const isToday = (iso) => !!iso && ymd(iso) === ymd();

/** "Fri", "Today", "Tomorrow", or the date for anything a week or more away. */
export const dueLabel = (iso) => {
  if (!iso) return "";
  const days = Math.round((Date.parse(ymd(iso)) - Date.parse(ymd())) / 86400000);
  if (days < 0) return "Overdue";
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  const d = new Date(iso);
  if (days < 7) return d.toLocaleDateString("en-GB", { weekday: "short", timeZone: zone() });
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: zone() });
};

export const greeting = () => {
  const h = Number(new Date().toLocaleString("en-US", { hour: "numeric", hour12: false, timeZone: zone() })) % 24;
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

/** Live "HH:MM:SS" until a time; null once it has passed. */
export function useCountdown(iso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!iso) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [iso]);
  if (!iso) return null;
  const left = Math.floor((new Date(iso).getTime() - now) / 1000);
  if (left <= 0) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(Math.floor(left / 3600))}:${pad(Math.floor((left % 3600) / 60))}:${pad(left % 60)}`;
}

/** Calls fn once when the time `iso` arrives (e.g. to reload when a class's Join opens). */
export const useAt = (iso, fn) => {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!iso) return undefined;
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0 || ms > 6 * 3600000) return undefined;
    const t = setTimeout(() => ref.current?.(), ms + 1000);
    return () => clearTimeout(t);
  }, [iso]);
};
